import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';

/**
 * CleverTap Web SDK (lifecycle marketing: journeys, on-site messages, web push).
 *
 * The browser half of the integration. The backend owns every event it can see
 * server-side (charges, renewals, feed generation, CV uploads) because those
 * must not depend on a tab staying open or an ad-blocker letting the script
 * through; the SDK owns what only a browser knows — sessions, page context and
 * the in-session events that trigger on-site journeys.
 *
 * SSR: the SDK touches `window`/`document` at load, so every method is a no-op
 * outside the browser. Nothing here may run during server rendering.
 *
 * Region: our project lives on CleverTap's DEFAULT ("global"/EU) cluster, whose
 * SDK configuration is NO region key at all — verified 2026-08-06 against the
 * API, where the same credentials authenticate on api.clevertap.com and 401 on
 * mec1.api.clevertap.com. Setting a region here would silently point the SDK at
 * a data centre that does not hold our account.
 */

/** CDN build of the Web SDK (CleverTap's documented https host). */
const SDK_URL = 'https://d2r1yp2w7bby2u.cloudfront.net/js/clevertap.min.js';

export interface CleverTapIdentity {
  /** candidates.id — the agreed CleverTap Identity. */
  id?: number | string | null;
  email?: string | null;
  phone?: string | null;
  name?: string | null;
}

@Injectable({ providedIn: 'root' })
export class CleverTapService {
  private initialised = false;

  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Install the command-queue stub, configure the account and inject the SDK.
   *
   * The stub is what makes early calls safe: `event`/`profile`/`onUserLogin`
   * pushes made before the script finishes loading are replayed by the SDK on
   * init, so callers never have to wait for it.
   */
  init(): void {
    const config = environment.cleverTap;

    if (!this.isBrowser || this.initialised) return;
    if (!config?.enabled || !config.accountId) return;

    this.initialised = true;

    try {
      const w = window as any;

      w.clevertap = w.clevertap || {
        event: [],
        profile: [],
        account: [],
        onUserLogin: [],
        notifications: [],
        privacy: [],
      };

      w.clevertap.account.push({ id: config.accountId });

      if (config.token) {
        w.clevertap.token = config.token;
      }

      // Single-page app: without this the SDK only counts the first page view,
      // and route changes (our whole checkout) never register.
      w.clevertap.spa = true;

      w.clevertap.privacy.push({ optOut: false });

      // CleverTap does NOT collect the device IP by default (GDPR), and without
      // it there is no city/region on the profile — which is what the Saudi
      // city segments are built on.
      w.clevertap.privacy.push({ useIP: true });

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = SDK_URL;
      script.onload = () => {
        if (config.debug) {
          // 3 = full request/response logging while validating events.
          w.clevertap.setLogLevel?.(3);
        }
      };
      document.head.appendChild(script);
    } catch {
      // Marketing tooling must never break the app.
      this.initialised = false;
    }
  }

  /**
   * Attach the session to a known person.
   *
   * `onUserLogin` (not `profile.push`) is what merges the anonymous profile
   * built before sign-in into the identified one — the pre-purchase browsing
   * and the member's later history end up on a single profile.
   */
  identify(identity: CleverTapIdentity): void {
    if (!this.ready()) return;

    const site: Record<string, unknown> = {};

    if (
      identity.id !== null &&
      identity.id !== undefined &&
      `${identity.id}` !== ''
    ) {
      site['Identity'] = `${identity.id}`;
    }
    if (identity.email) site['Email'] = identity.email.trim().toLowerCase();
    if (identity.phone) site['Phone'] = identity.phone;
    if (identity.name) site['Name'] = identity.name;

    // Nothing to key a profile on — pushing it would create a junk profile.
    if (!site['Identity'] && !site['Email']) return;

    this.push('onUserLogin', { Site: site });
  }

  /** Update profile properties for the current (already identified) user. */
  profile(properties: Record<string, unknown>): void {
    if (!this.ready() || !properties || Object.keys(properties).length === 0)
      return;

    this.push('profile', { Site: properties });
  }

  /** Record an event. Property names must avoid CleverTap's banned characters. */
  event(name: string, properties?: Record<string, unknown>): void {
    if (!this.ready() || !name) return;

    const w = window as any;

    try {
      if (properties && Object.keys(properties).length > 0) {
        w.clevertap.event.push(name, properties);
      } else {
        w.clevertap.event.push(name);
      }
    } catch {
      /* never break the caller */
    }
  }

  private push(queue: 'profile' | 'onUserLogin', payload: unknown): void {
    try {
      (window as any).clevertap[queue].push(payload);
    } catch {
      /* never break the caller */
    }
  }

  /** Browser + configured + the stub exists (init may have been skipped). */
  private ready(): boolean {
    return this.isBrowser && this.initialised && !!(window as any).clevertap;
  }
}
